import React, { useState, useEffect } from 'react';
import { API_BASE, apiHeaders, apiPost, leseFallback, zeigeApiFehler } from '../../lib/api';
import { toast } from '../../lib/toast';
import { LdSheet } from '../../components/ui/LdSheet';

interface Slot { id: number; datum: string; start_zeit: string; end_zeit: string; reihenfolge: number; }
interface Detail {
  vorschlag: { id: number; vorname: string; nachname: string; klasse_name: string | null; typ: string; antwort_aktion: string | null; antwort_confidence: number | null; antwort_begruendung: string | null; created_at: string };
  slots: Slot[];
  antwort_mail: { subject: string; sender_name: string | null; body_text: string | null; snippet: string | null; date: string } | null;
}

function fmt(d: string): string {
  return new Date(d + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
}
const AKTION_LABEL: Record<string, string> = {
  mehrere_offen: 'Mehrere offene Vorschläge', abgelehnt: 'Termin abgelehnt',
  neuer_vorschlag: 'Eltern schlagen anderen Termin vor', unklar: 'Antwort unklar',
};

export function SlotKlaerungModal({ vorschlagId, onClose, onResolved }: { vorschlagId: number | null; onClose: () => void; onResolved: () => void }) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [laden, setLaden]   = useState(true);
  const [wahl, setWahl]     = useState<string>(''); // 'slot:<id>' | 'ablehnen' | 'erledigt'
  const [busy, setBusy]     = useState(false);

  useEffect(() => {
    if (vorschlagId == null) return;
    setLaden(true); setWahl('');
    fetch(`${API_BASE}/api/klaerung/slot/${vorschlagId}`, { headers: apiHeaders() })
      .then(leseFallback<Detail | null>(null, '/api/klaerung/slot/:id'))
      .then(d => setDetail(d))
      .catch(() => {})
      .finally(() => setLaden(false));
  }, [vorschlagId]);

  if (vorschlagId == null) return null;

  async function speichern() {
    if (!detail || !wahl) { toast('Bitte eine Aktion wählen.', 'error'); return; }
    setBusy(true);
    try {
      if (wahl.startsWith('slot:')) {
        const slotId = Number(wahl.slice(5));
        await apiPost(`/api/terminvorschlaege/${vorschlagId}/bestaetigen-manuell`, { slot_id: slotId });
      } else if (wahl === 'ablehnen') {
        await apiPost(`/api/terminvorschlaege/${vorschlagId}/ablehnen-manuell`);
      } else {
        await apiPost(`/api/terminvorschlaege/${vorschlagId}/klaerung-erledigt`);
      }
      toast(wahl.startsWith('slot:') ? 'Termin bestätigt' : 'Erledigt', 'success');
      onResolved(); onClose();
    } catch (e) { zeigeApiFehler(e, 'Aktion fehlgeschlagen'); }
    finally { setBusy(false); }
  }

  const v = detail?.vorschlag;
  const conf = v?.antwort_confidence != null ? Math.round(v.antwort_confidence * 100) : null;
  const radioRow = (val: string, label: string) => (
    <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer', borderRadius: 10, border: wahl === val ? '1.5px solid var(--ld-primary)' : '1.5px solid var(--ld-outline-soft)', background: wahl === val ? 'var(--ld-primary-light, #EDE9F8)' : 'var(--ld-bg-input)', marginBottom: 8 }}>
      <input type="radio" checked={wahl === val} onChange={() => setWahl(val)} style={{ accentColor: 'var(--ld-primary)' }} />
      <span style={{ fontSize: 14, color: 'var(--ld-text-primary)' }}>{label}</span>
    </label>
  );

  return (
    <LdSheet open onClose={onClose} maxWidth={540} kopf={{ titel: 'Slot-Antwort prüfen' }}>
      <div>

        {laden ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--ld-text-secondary)' }}>Lädt …</div>
        ) : !detail || !v ? (
          <div style={{ color: 'var(--ld-text-secondary)' }}>Nicht gefunden.</div>
        ) : (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{v.vorname} {v.nachname}{v.klasse_name ? ` (${v.klasse_name})` : ''}</div>

            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', letterSpacing: .4, marginBottom: 6 }}>VORGESCHLAGEN ({fmt(detail.slots[0]?.datum ?? '')} …)</div>
            <div style={{ fontSize: 13, color: 'var(--ld-text-primary)', marginBottom: 14, lineHeight: 1.6 }}>
              {detail.slots.map(s => <div key={s.id}>• {fmt(s.datum)} {s.start_zeit}–{s.end_zeit}</div>)}
            </div>

            {detail.antwort_mail && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', letterSpacing: .4, marginBottom: 6 }}>Eltern-Antwort</div>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, background: 'var(--ld-bg-input)', borderRadius: 8, padding: '10px 12px', margin: '0 0 14px', fontFamily: 'inherit', maxHeight: 140, overflowY: 'auto' }}>{(detail.antwort_mail.body_text || detail.antwort_mail.snippet || '').slice(0, 600)}</pre>
              </>
            )}

            <div style={{ background: 'var(--ld-primary-soft)', borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 12, color: 'var(--ld-primary)', lineHeight: 1.5 }}>
              Vermutung der KI: {AKTION_LABEL[v.antwort_aktion ?? ''] ?? 'unklar'}{conf != null ? ` (${conf}%)` : ''}
              {v.antwort_begruendung ? <><br />„{v.antwort_begruendung}"</> : null}
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', marginBottom: 8 }}>Was möchtest du tun?</div>
            {detail.slots.map(s => radioRow(`slot:${s.id}`, `${fmt(s.datum)} ${s.start_zeit} bestätigen`))}
            {radioRow('ablehnen', 'Vorschlag ablehnen')}
            {radioRow('erledigt', 'Zur Kenntnis genommen (schließen)')}

            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button onClick={onClose} style={{ flex: 1, padding: '12px 0', borderRadius: 12, border: '1.5px solid var(--ld-outline-soft)', background: 'var(--ld-bg-input)', fontSize: 15, fontWeight: 600, cursor: 'pointer', color: 'var(--ld-text-secondary)' }}>Abbrechen</button>
              <button onClick={speichern} disabled={busy || !wahl} style={{ flex: 2, padding: '12px 0', borderRadius: 12, border: 'none', background: busy || !wahl ? 'var(--ld-primary-soft-strong)' : 'var(--ld-primary)', color: 'white', fontSize: 15, fontWeight: 700, cursor: busy || !wahl ? 'default' : 'pointer' }}>
                {busy ? '…' : 'Speichern'}
              </button>
            </div>
          </div>
        )}
      </div>
    </LdSheet>
  );
}
